import * as React from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import propz from 'propz';
import { checkAvailabilityTeamName } from '../../../../../../helpers/service/admin/schoolTeams';
import { AppUser } from '../../../../../App/App';
import { TEAM_TYPE_TYPE } from '../../../../../../types/tournamentTeam';
import { GENDER_TYPE } from '../../../../../../types/common';
import './SchoolEventSaveTeamsForm.scss';

interface Props {
  teamsAdhoc: TeamAdhoc[];
  user: AppUser;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

interface TeamAdhoc {
  ages: number[];
  gender: GENDER_TYPE;
  name: string;
  players: Player[];
  schoolId: string;
  sportId: string;
  teamType: TEAM_TYPE_TYPE;
  houseId?: string;
}

interface Player {
  userId: string;
  permissionId: string;
}

export function SchoolEventSaveTeamsForm(props: Props) {
  const { teamsAdhoc, user, onSubmit, onCancel } = props;

  const initialValues = {
    teams: teamsAdhoc.map(teamAdhoc => ({
      name: teamAdhoc.name || '',
      isSave: false
    }))
  };

  const sportId = propz.get(teamsAdhoc, [0, 'sportId']);

  const SchoolEventTeamsSchema = Yup.object().shape({
    teams: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().test(
          'name',
          'This team name has already been used for your chosen sport, please use a different name',
          function(value) {
            const isSave = propz.get(this.parent, ['isSave'], false);

            if (!isSave) {
              return true;
            }

            if (!value || !String(value).trim()) {
              return this.createError({ message: 'Enter a team name' });
            }

            return checkAvailabilityTeamName(user, {
              name: String(value).trim(),
              sportId: sportId
            }).then(res => {
              return res.isAvailable;
            });
          }
        ),
        isSave: Yup.boolean()
      })
    )
  });

  const getPlayerCountText = (count: number) => {
    return count === 1 ? '1 player' : `${count} players`;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={SchoolEventTeamsSchema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={values => {
        const valuesUpdated = values.teams.map((team, index) => ({
          ...teamsAdhoc[index],
          ...team,
          name: String(team.name || '').trim()
        }));

        onSubmit(valuesUpdated);
      }}
    >
      {({ setFieldValue, values }) => (
        <Form className="eSaveEventTeams">
          <header className="eSaveEventTeams__header">
            <span className="eSaveEventTeams__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </span>
            <div className="eSaveEventTeams__headerCopy">
              <h2 className="eSaveEventTeams__title">Save newly created team{teamsAdhoc.length > 1 ? 's' : ''}</h2>
              <p className="eSaveEventTeams__subtitle">
                Choose which new team{teamsAdhoc.length > 1 ? 's' : ''} should be saved to your school team list, and
                which should be used only for this event.
              </p>
            </div>
            <button type="button" className="eSaveEventTeams__close" onClick={onCancel} aria-label="Close">
              ×
            </button>
          </header>

          <div className="eSaveEventTeams__body">
            {values.teams.map((team, index) => {
              const sourceTeam = teamsAdhoc[index];
              const playerCount = propz.get(sourceTeam, ['players', 'length'], 0);

              return (
                <section key={`team_${index}`} className="eSaveEventTeams__team">
                  <div className="eSaveEventTeams__teamHead">
                    <div className="eSaveEventTeams__teamCopy">
                      <div className="eSaveEventTeams__eyebrow">New team {teamsAdhoc.length > 1 ? index + 1 : ''}</div>
                      <div className="eSaveEventTeams__teamName">{sourceTeam.name || 'Untitled team'}</div>
                    </div>
                    <div className="eSaveEventTeams__teamControls">
                      <div className="eSaveEventTeams__playerCount">{getPlayerCountText(playerCount)}</div>
                      <label className="eSaveEventTeams__switch">
                        <input
                          type="checkbox"
                          checked={team.isSave}
                          onChange={event => setFieldValue(`teams.${index}.isSave`, event.target.checked)}
                          aria-label={`Save ${sourceTeam.name || `team ${index + 1}`} to the school team list`}
                        />
                        <span className="eSaveEventTeams__switchTrack" aria-hidden="true">
                          <span />
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="eSaveEventTeams__teamBody">
                    <div className="eSaveEventTeams__choiceTitle">
                      {team.isSave ? 'Save to school team list' : 'Use only for this event'}
                    </div>
                    <div className="eSaveEventTeams__choiceText">
                      {team.isSave
                        ? 'This team can be reused for future events.'
                        : 'This team will exist only inside this event.'}
                    </div>

                    {team.isSave && (
                      <div className="eSaveEventTeams__nameField">
                        <label className="eSaveEventTeams__label">Team name</label>
                        <Field
                          name={`teams.${index}.name`}
                          className="form-control eSaveEventTeams__input"
                          placeholder="Enter team name"
                        />
                        <ErrorMessage
                          component="div"
                          className="alert alert-danger mt-2"
                          name={`teams.${index}.name`}
                        />
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>

          <footer className="eSaveEventTeams__footer">
            <button type="button" className="eSaveEventTeams__cancel" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="eSaveEventTeams__submit">
              Save choices
            </button>
          </footer>
        </Form>
      )}
    </Formik>
  );
}
